import _ from 'underscore';

import { Formatters, LocaleData } from '../../codegen/Localize';

interface ITranslateInterpolation {
  setLocale(locale: string): void;
  getInterpolationIdentifier(): string;
  useSanitizeValueStrategy(value: string): ITranslateInterpolation;
  interpolate(input: string, params: any): string;
}

export module L10n {

  export let language: string;
  export let locale: string;

  export function localizedCount(value: any) {
    if (value === +value) { // is value a numeric type?
      if (value === (value|0)) { // is value an integer?
        return Formatters.formatCount(value);
      }
    }
    return value;
  }

  export function localizedFloat(value: any) {
    if (value === +value) { // is value a numeric type?
      return Formatters.formatFloat(value);
    }
    return value;
  }

  export function localizedPercent(value: any) {
    if (value === +value) { // is value a numeric type?
      return Formatters.formatPercent(value / 100);
    }
    return value;
  }

  function formatAsDate(value: any, formatter: (date: Date) => string): string {
    if (!formatter) return '';
    if (_.isDate(value)) {
      return formatter(value);
    } else if (_.isString(value)) {
      return formatter(new Date(value));
    }
    return '';
  }

  export function localizedDate(value: any) {
    return formatAsDate(value, Formatters.formatMediumDate);
  }

  export function localizedDateTime(value: any) {
    return formatAsDate(value, Formatters.formatMediumDateTimeWithoutSeconds);
  }

  export function localizedShortTime(value: any) {
    return formatAsDate(value, Formatters.formatShortTime);
  }

  // returns 0 for Sunday, 1 for Monday
  export function firstDayOfWeek(): number {
    return LocaleData.get().firstDayOfWeek === 'mon' ? 1 : 0;
  }

  // True if Monday, false if Sunday. Callers shouldn't need to know the int values.
  export function firstDayOfWeekIsMonday(): boolean {
    return LocaleData.get().firstDayOfWeek === 'mon';
  }

  export function is24HourTimeCycle(): boolean {
    return LocaleData.get().prefer24HourTimeCycle;
  }

  export function narrowDayNames(): string[] {
    const narrowDayNames = _.values(LocaleData.get().narrowDayNames);
    if (firstDayOfWeekIsMonday()) {
      narrowDayNames.push(narrowDayNames.shift());
    }
    return narrowDayNames;
  }

  export function wideDayNames(): string[] {
    const wideDayNames = _.values(LocaleData.get().wideDayNames);
    if (firstDayOfWeekIsMonday()) {
      wideDayNames.push(wideDayNames.shift());
    }
    return wideDayNames;
  }

  export function yearAndMonth(date: Date): string {
    return Formatters.formatYearAndMonth(date);
  }

  export function elideIfLong(s: string, maxLength: number = 100): string {
    const HORIZONTAL_ELLIPSIS = '\u2026';
    return s && s.length > maxLength
      ?  s.substr(0, maxLength) +  HORIZONTAL_ELLIPSIS
      : s;
  }

  class SanitizingTranslateInterpolationWrapper implements ITranslateInterpolation {

    private savedSanitizeStrategy: string;

    constructor(
      private wrappedInterpolation: ITranslateInterpolation,
      private id: string = '',
      private sanitizeStrategy: string = 'escaped'
    ) {}

    public setLocale(locale: string): void {
      this.wrappedInterpolation.setLocale(locale);
    }

    public getInterpolationIdentifier(): string {
      return this.id;
    }

    public useSanitizeValueStrategy(value: string): ITranslateInterpolation {
      this.savedSanitizeStrategy = value;
      this.wrappedInterpolation.useSanitizeValueStrategy(value);
      return this;
    }

    public interpolate(input: string, params?: any): string {
      this.wrappedInterpolation.useSanitizeValueStrategy(this.sanitizeStrategy);
      const result = this.wrappedInterpolation.interpolate(input, params);
      this.wrappedInterpolation.useSanitizeValueStrategy(this.savedSanitizeStrategy);
      return result;
    }

  }

  class NumberLocalizingTranslateInterpolationWrapper implements ITranslateInterpolation {

    private savedSanitizeStrategy: string;

    constructor(
      private wrappedInterpolation: ITranslateInterpolation,
      private id: string,
      private localize: (value: any) => string,
      private sanitizeStrategy: string = null
    ) {}

    public setLocale(locale: string): void {
      this.wrappedInterpolation.setLocale(locale);
    }

    public getInterpolationIdentifier(): string {
      return this.id;
    }

    public useSanitizeValueStrategy(value: string): ITranslateInterpolation {
      this.savedSanitizeStrategy = value;
      this.wrappedInterpolation.useSanitizeValueStrategy(value);
      return this;
    }

    public interpolate(input: string, params?: any): string {
      const newParams: any = {};
      _.each(params, (value: any, key: string) => {
        newParams[key] = value;
        const locKey = key + '_LOC';
        if (!_.has(params, locKey) && value === +value) {
          newParams[locKey] = this.localize(value);
        }
      });
      this.wrappedInterpolation.useSanitizeValueStrategy(this.sanitizeStrategy);
      const result = this.wrappedInterpolation.interpolate(input, newParams);
      this.wrappedInterpolation.useSanitizeValueStrategy(this.savedSanitizeStrategy);
      return result;
    }

  }
  /**
   * When to use escaped vs non:
   *   If you are using the translate directive in pug, use the escaped version
   *   If you are binding to a string that was localized with the $translate service, use the non-escaped version.
   *
   * Details:
   *   Angular will escape the results of expressions before rendering them in the DOM (https://docs.angularjs.org/api/ng/service/$sce)
   *   The translate directive does not escape strings. This is fine for static strings, but we need to use the escaped versions
   *   of the interpolators to ensure that any dynamic data gets santized.
   */
  export let Interpolations = {
    Escaped: 'escaped',
    Count: 'localizedCount',
    CountEscaped: 'localizedCountEscaped',
    Float: 'localizedFloat',
    FloatEscaped: 'localizedFloatEscaped'
  };

}
